import { isPlatformBrowser } from '@angular/common'
import { inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { filter } from 'rxjs/operators'

/**
 * Analytics Service for tracking events in Google Analytics and Meta Pixel
 * Handles both pageviews and custom events with SSG/SSR safety
 * Only tracks events in production mode
 */
@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    private platformId = inject(PLATFORM_ID)
    private router = inject(Router)
    private isBrowser: boolean
    private isProduction: boolean

    constructor() {
        this.isBrowser = isPlatformBrowser(this.platformId)
        this.isProduction = !isDevMode()

        if (this.isBrowser) {
            this.initRouteTracking()
        }
    }

    /**
     * Track page navigation for Single Page Application
     */
    private initRouteTracking(): void {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.trackPageView(event.urlAfterRedirects)
        })
    }

    /**
     * Track a pageview in GA and Meta Pixel
     */
    trackPageView(url: string): void {
        if (!this.isBrowser) return

        if (!this.isProduction) {
            console.log(`📊 [DEV] Pageview tracked: ${url}`)
            return
        }

        const pageviewParams = {
            page_path: url,
            page_title: document.title,
            page_location: window.location.href
        }

        // Google Analytics pageview
        if (typeof gtag !== 'undefined') {
            gtag('event', 'page_view', pageviewParams)
        }

        // Meta Pixel pageview
        if (typeof fbq !== 'undefined') {
            fbq('track', 'PageView', pageviewParams)
        }
    }

    /**
     * Track a custom event in both GA and Meta Pixel
     */
    trackEvent(eventName: string, params?: Record<string, any>): void {
        if (!this.isBrowser) return

        // Log in dev mode but don't send to analytics
        if (!this.isProduction) {
            console.log(`📊 [DEV] Event tracked: ${eventName}`, params)
            return
        }

        // Google Analytics custom event
        if (typeof gtag !== 'undefined') {
            gtag('event', eventName, params || {})
        }

        // Meta Pixel custom event
        if (typeof fbq !== 'undefined') {
            fbq('track', eventName, params || {})
        }
    }

    /**
     * Track clicks on CTA buttons
     */
    trackCTAClick(ctaName: string, location?: string): void {
        this.trackEvent('cta_click', {
            cta_name: ctaName,
            location: location || window.location.pathname
        })
    }

    /**
     * Track contact form interactions
     */
    trackContactInteraction(action: 'open' | 'submit' | 'calendly_open'): void {
        const eventMap = {
            open: 'Contact',
            submit: 'CompleteRegistration',
            calendly_open: 'Schedule'
        }

        this.trackEvent(eventMap[action], {
            content_category: 'contact',
            action: action
        })
    }

    /**
     * Track navigation clicks
     */
    trackNavigation(destination: string, source?: string): void {
        this.trackEvent('navigation_click', {
            destination,
            source: source || 'nav_menu'
        })
    }

    /**
     * Track external link clicks
     */
    trackExternalLink(url: string, linkText?: string): void {
        this.trackEvent('external_link_click', {
            link_url: url,
            link_text: linkText,
            outbound: true
        })
    }

    /**
     * Track blog post interactions
     */
    trackBlogInteraction(action: 'view' | 'read_time', postSlug: string, metadata?: Record<string, any>): void {
        this.trackEvent(`blog_${action}`, {
            post_slug: postSlug,
            ...metadata
        })
    }

    /**
     * Track portfolio/project views
     */
    trackProjectView(projectName: string): void {
        this.trackEvent('project_view', {
            project_name: projectName,
            content_type: 'portfolio'
        })
    }

    /**
     * Track service page views
     */
    trackServiceView(serviceName: string): void {
        this.trackEvent('service_view', {
            service_name: serviceName
        })
    }

    /**
     * Track language changes
     */
    trackLanguageChange(language: string): void {
        this.trackEvent('language_change', {
            language
        })
    }

    /**
     * Track scroll depth for engagement
     */
    trackScrollDepth(percentage: number): void {
        this.trackEvent('scroll_depth', {
            percentage,
            page: window.location.pathname
        })
    }
}

// Type declarations for global functions
declare global {
    function gtag(...args: any[]): void
    function fbq(...args: any[]): void
}
